/**
 * GameLoop.ts — fixed-timestep accumulator.
 *
 * Live play and offline catch-up funnel through the SAME per-tick call, so there
 * is no special-cased math and no online/offline drift. `tickFn` receives a fixed
 * dt (never wall-clock dt), and production is computed as rate * dt/1000.
 */
import { clamp } from './numbers';
import { BALANCE } from '../data';
import type { GameState } from './GameState';
import type { EventBus } from './EventBus';
import type { Clock } from './clock';

export interface TickContext {
  bus: EventBus;
  clock: Clock;
}

export type TickFn = (state: GameState, dtMs: number, ctx: TickContext) => void;

const TICK_MS = BALANCE.tickMs;

export class GameLoop {
  private accumulator = 0;
  private rafId: number | null = null;
  private lastFrame = 0;

  constructor(
    private state: GameState,
    private tickFn: TickFn,
    private ctx: TickContext,
    private onFrame?: () => void,
  ) {}

  /**
   * Advance the simulation by `elapsedMs` of real time (clamped to avoid the
   * spiral of death). Runs whole fixed ticks; sub-tick remainder is carried in
   * the accumulator. Returns the number of ticks executed.
   */
  advance(elapsedMs: number): number {
    this.accumulator += clamp(elapsedMs, 0, BALANCE.maxFrameMs);
    let ticks = 0;
    while (this.accumulator >= TICK_MS) {
      this.runTick();
      this.accumulator -= TICK_MS;
      ticks++;
    }
    return ticks;
  }

  private runTick(): void {
    this.tickFn(this.state, TICK_MS, this.ctx);
    this.state.meta.gameTimeMs += TICK_MS;
    this.state.meta.playtimeMs += TICK_MS;
  }

  /** Start the requestAnimationFrame-driven live loop (browser only). */
  start(): void {
    if (this.rafId !== null) return;
    this.lastFrame = this.ctx.clock.now();
    const frame = (): void => {
      const now = this.ctx.clock.now();
      const dt = now - this.lastFrame;
      this.lastFrame = now;
      this.advance(dt);
      this.onFrame?.();
      this.rafId = requestAnimationFrame(frame);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}

/**
 * Run an offline catch-up over `elapsedMs` of real time. Credits at the
 * configured efficiency and is capped at the offline cap. Uses the identical
 * per-tick path as live play. Returns { ticks, creditedMs }.
 */
export function runCatchUp(
  state: GameState,
  elapsedMs: number,
  tickFn: TickFn,
  ctx: TickContext,
  efficiency: number = BALANCE.offlineEfficiency,
): { ticks: number; creditedMs: number } {
  const capped = clamp(elapsedMs, 0, BALANCE.offlineCapMs);
  const effective = capped * clamp(efficiency, 0, 1);
  const ticks = Math.floor(effective / TICK_MS);
  for (let i = 0; i < ticks; i++) {
    tickFn(state, TICK_MS, ctx);
    state.meta.gameTimeMs += TICK_MS;
    state.meta.playtimeMs += TICK_MS;
  }
  return { ticks, creditedMs: ticks * TICK_MS };
}
